import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { ToroEnums } from '../../../common/enumerations/toro.enums';
import SaVarianceColor = ToroEnums.SaVarianceColor;

@Component({
    selector: 'toro-variance-color-range',
    templateUrl: './variance-color-range.component.html',
    styleUrls: ['./variance-color-range.component.less']
})
export class VarianceColorRangeComponent implements OnInit {
    private readonly MULTIPLIER: number = 1;
    // private readonly MAX_RANGE = 100;

    private readonly THRESHOLD_MIN = -50;
    private readonly THRESHOLD_MAX = 50;
    private readonly TOTAL_RANGE = this.THRESHOLD_MAX - this.THRESHOLD_MIN;

    @Output() dragEnd = new EventEmitter();

    private _range: { from: number, to: number, color: string }[]
    @Input() set range(value: { from: number, to: number, color: string }[]) {
        if (value == null || value.length < 3) { return; }

        this._range = value;

        if (this.ranges == null) {
            this.initRangeSelector();
        }
    }

    get range(): { from: number, to: number, color: string }[] {
        return this._range;
    }

    private isActive = false;
    protected ranges: { value: number, length: number, color: string }[];
    protected markers: { position: number }[];
    draggingMarkerIndex: number | null = null;

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    ngOnInit(): void {
        if (this.range != null) {
            this.initRangeSelector();
        }
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private initRangeSelector() {
        const r1Boundary: number = this.range[0].to;
        const r2Boundary: number = this.range[1].to;

        const r1Length = Math.abs(this.THRESHOLD_MIN - r1Boundary) / this.TOTAL_RANGE * 100;
        const r2Length = Math.abs(r2Boundary - r1Boundary) / this.TOTAL_RANGE * 100;

        // Define ranges
        this.ranges = [
            { value: r1Boundary * this.MULTIPLIER, length: r1Length, color: SaVarianceColor.R1 },         // 0 - 5
            { value: r2Boundary * this.MULTIPLIER, length: r2Length, color: SaVarianceColor.R2 },       // 6 - 19
        ];

        // Define markers
        this.markers = [
            { position: r1Length }, // Marker 1
            { position: r1Length + r2Length }, // Marker 2
        ];
    }

    // Handle dragging start
    startDragging(event: MouseEvent | TouchEvent, markerIndex: number) {
        event.preventDefault();
        this.draggingMarkerIndex = markerIndex;
    }

    // Handle dragging end
    @HostListener('document:mouseup')
    @HostListener('document:touchend')
    stopDragging() {
        if (this.isActive) {
            this.isActive = false;
            this.draggingMarkerIndex = null;
            this.dragEnd.emit();
        }
    }

    // Handle dragging move
    @HostListener('document:mousemove', ['$event'])
    @HostListener('document:touchmove', ['$event'])
    dragMarker(event: MouseEvent | TouchEvent) {
        if (this.draggingMarkerIndex === null) return;

        this.isActive = true;

        // Determine the cursor position
        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
        const container = (event.target as HTMLElement).closest('.range-container') as HTMLElement;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        let percentage = Math.max(0, Math.min(this.TOTAL_RANGE, ((clientX - rect.left) / rect.width) * 100));

        // Update the marker position
        const index = this.draggingMarkerIndex;
        const min = index === 0 ? this.THRESHOLD_MIN : this.markers[index - 1].position + 1;
        const max = index === this.markers.length - 1 ? (this.TOTAL_RANGE - 1) : this.markers[index + 1].position - 1;

        this.markers[index].position = Math.max(min, Math.min(max, percentage));
        this.updateRanges();
    }

    // Update ranges based on marker positions
    updateRanges() {
        this.ranges[0].length = this.markers[0].position;
        this.ranges[0].value = this.THRESHOLD_MIN + (this.TOTAL_RANGE * (this.markers[0].position / 100));

        this.ranges[1].length = this.markers[1].position - this.markers[0].position;
        this.ranges[1].value = this.THRESHOLD_MIN + (this.TOTAL_RANGE * (this.markers[1].position / 100));

        const displayValues = [
            Math.round(this.ranges[0].value),
            Math.round(this.ranges[1].value),
        ];

        this.range[0].to = displayValues[0];

        this.range[1].from = displayValues[0] + 1;
        this.range[1].to = displayValues[1];

        this.range[2].from = displayValues[1];

        // console.log(`0.to: ${this.range[0].to}, 1.from: ${this.range[1].from}`)
    }
}
